import React, { useState, useEffect } from 'react';
import { searchSkins, searchComponentSkins, fetchOwnedSkins, fetchOwnedComponentSkins, purchaseSkin, fetchSkinDetail } from '../services/skinApi';

const SkinPurchaseModal = ({ 
  isOpen, 
  onClose, 
  onPurchaseSuccess, 
  onPurchaseComplete, // ComponentSkinSelector용 콜백
  skinType = 'LAYOUT',
  componentType = null // 컴포넌트 타입 (LOGIN, BUTTON 등)
}) => {
  // componentType을 안전한 문자열로 변환
  const safeComponentType = (() => {
    if (!componentType) return null;
    if (typeof componentType === 'string') return componentType;
    if (typeof componentType === 'object') {
      if (typeof componentType.id === 'string') return componentType.id;
      if (typeof componentType.name === 'string') return componentType.name;
      if (typeof componentType.displayName === 'string') return componentType.displayName;
      return 'COMPONENT';
    }
    return 'COMPONENT';
  })();
  const [skins, setSkins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkin, setSelectedSkin] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('browse'); // 'browse' or 'detail'
  const [skinViewMode, setSkinViewMode] = useState('owned'); // 'owned' or 'available'

  // 스킨 목록 로드
  const loadSkins = async (query = '') => {
    setLoading(true);
    setError('');
    try {
      let skinsData;
      if (skinType === 'COMPONENT' && safeComponentType) {
        if (skinViewMode === 'owned') {
          // 보유한 컴포넌트 스킨 조회
          skinsData = await fetchOwnedComponentSkins(safeComponentType);
        } else {
          // 구매 가능한 모든 컴포넌트 스킨 조회
          skinsData = await searchComponentSkins(safeComponentType, query);
        }
        
        // 클라이언트 사이드에서 검색어 필터링 (보유한 스킨의 경우)
        if (skinViewMode === 'owned' && query) {
          skinsData = skinsData.filter(skin => {
            const skinName = typeof skin.name === 'string' ? skin.name : 
                           typeof skin.name === 'object' ? (skin.name.name || skin.name.displayName || '') : '';
            const skinDescription = typeof skin.description === 'string' ? skin.description : '';
            return skinName.toLowerCase().includes(query.toLowerCase()) ||
                   skinDescription.toLowerCase().includes(query.toLowerCase());
          });
        }
      } else {
        if (skinViewMode === 'owned') {
          // 보유한 레이아웃 스킨 조회
          skinsData = await fetchOwnedSkins(skinType);
        } else {
          // 구매 가능한 모든 레이아웃 스킨 조회
          skinsData = await searchSkins(skinType, query);
        }
        
        // 클라이언트 사이드에서 검색어 필터링 (보유한 스킨의 경우)
        if (skinViewMode === 'owned' && query) {
          skinsData = skinsData.filter(skin => {
            const skinName = typeof skin.name === 'string' ? skin.name : 
                           typeof skin.name === 'object' ? (skin.name.name || skin.name.displayName || '') : '';
            const skinDescription = typeof skin.description === 'string' ? skin.description : '';
            return skinName.toLowerCase().includes(query.toLowerCase()) ||
                   skinDescription.toLowerCase().includes(query.toLowerCase());
          });
        }
      }
      setSkins(skinsData);
    } catch (error) {
      setError('스킨 목록을 불러오는데 실패했습니다.');
      console.error('스킨 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  // 모달이 열릴 때 스킨 목록 로드
  useEffect(() => {
    if (isOpen) {
      loadSkins();
      setSearchQuery('');
      setSelectedSkin(null);
      setActiveTab('browse');
      setError(''); // 모달 열릴 때 에러 메시지 초기화
      setPurchasing(false); // 구매 상태도 초기화
    }
  }, [isOpen, skinType, safeComponentType, skinViewMode]);

  // 검색 처리
  const handleSearch = (e) => {
    e.preventDefault();
    setError(''); // 검색 시 에러 메시지 초기화
    loadSkins(searchQuery);
  };

  // 스킨 선택 및 상세보기
  const handleSelectSkin = async (skin) => {
    setError(''); // 새로운 스킨 선택 시 에러 메시지 초기화
    try {
      const detailData = await fetchSkinDetail(skin.id);
      setSelectedSkin({ ...skin, ...detailData });
      setActiveTab('detail');
    } catch (error) {
      console.error('스킨 상세 정보 로드 실패:', error);
      setSelectedSkin(skin);
      setActiveTab('detail');
    }
  };

  // 스킨 구매
  const handlePurchase = async (skinId) => {
    setPurchasing(true);
    setError('');
    try {
      console.log(`[SkinPurchaseModal] 스킨 구매 시작: ${skinId}`);
      await purchaseSkin(skinId);
      console.log(`[SkinPurchaseModal] 스킨 구매 성공: ${skinId}`);
      
      // 레이아웃 스킨용 콜백
      if (onPurchaseSuccess) {
        console.log(`[SkinPurchaseModal] onPurchaseSuccess 콜백 호출`);
        onPurchaseSuccess(selectedSkin);
      }
      
      // 컴포넌트 스킨용 콜백
      if (onPurchaseComplete) {
        console.log(`[SkinPurchaseModal] onPurchaseComplete 콜백 호출`);
        onPurchaseComplete();
      }
      
      onClose();
    } catch (error) {
      console.error('구매 오류:', error);
      
      // 서버에서 받은 에러 메시지 처리
      let errorMessage = '구매에 실패했습니다. 다시 시도해주세요.';
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.payload && errorData.payload.message) {
          errorMessage = errorData.payload.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setPurchasing(false);
    }
  };

  // 모달이 닫혀있으면 렌더링하지 않음
  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .skin-modal-overlay {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0 !important;
            margin: 0 !important;
            z-index: 1050;
            background-color: rgba(0,0,0,0.6);
            box-sizing: border-box;
          }
          .skin-modal-content {
            width: min(95vw, 1400px);
            height: min(90vh, 800px);
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.3);
            display: flex;
            flex-direction: column;
            overflow: hidden;
            margin: 0 auto;
          }
        `}
      </style>
      <div 
        className="skin-modal-overlay"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div 
          className="skin-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
        <div style={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div className="modal-header" style={{ 
            borderBottom: '1px solid #dee2e6',
            padding: '1rem 1.5rem',
            flexShrink: 0
          }}>
            <h4 className="modal-title mb-0">
              {activeTab === 'browse' ? 
                (skinType === 'COMPONENT' && safeComponentType ? 
                  `${safeComponentType} 컴포넌트 스킨` : 
                  '스킨 목록'
                ) : 
                (selectedSkin ? (typeof selectedSkin.name === 'object' ? selectedSkin.name.name || selectedSkin.name.displayName || 'Unknown' : selectedSkin.name) : '')
              }
            </h4>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={purchasing}
            ></button>
          </div>
          
          <div className="modal-body" style={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '1.5rem'
          }}>
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {activeTab === 'browse' && (
              <>
                {/* 탭 선택 영역 */}
                <div className="mb-4">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${skinViewMode === 'owned' ? 'active' : ''}`}
                        type="button"
                        role="tab"
                        onClick={() => {
                          setSkinViewMode('owned');
                          setSearchQuery('');
                          setError('');
                        }}
                      >
                        보유 스킨
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${skinViewMode === 'available' ? 'active' : ''}`}
                        type="button"
                        role="tab"
                        onClick={() => {
                          setSkinViewMode('available');
                          setSearchQuery('');
                          setError('');
                        }}
                      >
                        구매 가능한 스킨
                      </button>
                    </li>
                  </ul>
                </div>

                {/* 검색 영역 */}
                <form onSubmit={handleSearch} className="mb-4">
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={skinType === 'COMPONENT' && safeComponentType ? 
                        `${skinViewMode === 'owned' ? '보유중인' : '구매 가능한'} ${safeComponentType} 스킨 검색...` : 
                        `${skinViewMode === 'owned' ? '보유중인' : '구매 가능한'} 스킨 검색...`
                      }
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button className="btn btn-outline-secondary" type="submit">
                      검색
                    </button>
                  </div>
                </form>

                {/* 스킨 목록 */}
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">로딩중...</span>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    {skins.length === 0 ? (
                      <div className="col-12">
                        <p className="text-muted text-center">
                          {skinViewMode === 'owned' ? '보유중인 스킨이 없습니다.' : '구매 가능한 스킨이 없습니다.'}
                        </p>
                      </div>
                    ) : (
                      skins.map((skin) => (
                        <div key={skin.id} className="col-xl-3 col-lg-4 col-md-6 mb-4">
                          <div className="card h-100" style={{ minHeight: '380px' }}>
                            {skin.thumbnail && (
                              <img
                                src={skin.thumbnail}
                                className="card-img-top"
                                alt={typeof skin.name === 'object' ? skin.name.name || skin.name.displayName || 'Unknown' : skin.name}
                                style={{ height: '180px', objectFit: 'cover' }}
                              />
                            )}
                            <div className="card-body d-flex flex-column">
                              <h6 className="card-title" style={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {typeof skin.name === 'object' ? skin.name.name || skin.name.displayName || 'Unknown' : skin.name}
                              </h6>
                              <p className="card-text text-muted small flex-grow-1" style={{ 
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                lineHeight: '1.4em',
                                maxHeight: '4.2em'
                              }}>
                                {typeof skin.description === 'object' ? 'Description is an object' : (skin.description || '설명이 없습니다.')}
                              </p>
                              <div className="d-flex justify-content-between align-items-center mt-2" style={{ minHeight: '2rem' }}>
                                <span className="badge bg-secondary" style={{ 
                                  fontSize: '0.75em',
                                  maxWidth: '100px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {skinType === 'COMPONENT' ? 
                                    (typeof skin.componentType === 'object' ? 'COMPONENT' : (skin.componentType || safeComponentType || 'COMPONENT')) : 
                                    (typeof skin.layoutCategory === 'object' ? 'GENERAL' : (skin.layoutCategory || 'GENERAL'))
                                  }
                                </span>
                                <span className="fw-bold" style={{ 
                                  fontSize: '0.9em',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {skin.price === 0 ? '무료' : `₩${skin.price.toLocaleString()}`}
                                </span>
                              </div>
                              <div className="mt-auto pt-2">
                                {skinViewMode === 'owned' || skin.hasPermission ? (
                                  <button
                                    className="btn btn-success btn-sm w-100 position-relative"
                                    onClick={() => handleSelectSkin(skin)}
                                    style={{ 
                                      whiteSpace: 'nowrap',
                                      background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                                      border: 'none',
                                      color: 'white',
                                      fontWeight: '600',
                                      boxShadow: '0 2px 8px rgba(25, 135, 84, 0.3)',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.transform = 'translateY(-1px)';
                                      e.target.style.boxShadow = '0 4px 12px rgba(25, 135, 84, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = 'translateY(0)';
                                      e.target.style.boxShadow = '0 2px 8px rgba(25, 135, 84, 0.3)';
                                    }}
                                  >
                                    <i className="fas fa-check-circle me-2" style={{ fontSize: '0.9em' }}></i>
                                    보유중 · 상세보기
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-primary btn-sm w-100"
                                    onClick={() => handleSelectSkin(skin)}
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    구매하기 · 상세보기
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}

            {activeTab === 'detail' && selectedSkin && (
              <div>
                <button
                  className="btn btn-link p-0 mb-3"
                  onClick={() => {
                    setActiveTab('browse');
                    setError(''); // 목록으로 돌아갈 때 에러 메시지 초기화
                  }}
                >
                  ← 목록으로 돌아가기
                </button>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    {selectedSkin.thumbnail && (
                      <img
                        src={selectedSkin.thumbnail}
                        className="img-fluid rounded"
                        alt={typeof selectedSkin.name === 'object' ? selectedSkin.name.name || selectedSkin.name.displayName || 'Unknown' : selectedSkin.name}
                        style={{ maxHeight: '400px', width: '100%', objectFit: 'cover' }}
                      />
                    )}
                  </div>
                  <div className="col-md-6">
                    <h5>{typeof selectedSkin.name === 'object' ? selectedSkin.name.name || selectedSkin.name.displayName || 'Unknown' : selectedSkin.name}</h5>
                    <p className="text-muted">{typeof selectedSkin.description === 'object' ? 'Description is an object' : selectedSkin.description}</p>
                    
                    <div className="mb-3">
                      <span className="badge bg-secondary me-2">
                        {skinType === 'COMPONENT' ? 
                          (typeof selectedSkin.componentType === 'object' ? 'COMPONENT' : (selectedSkin.componentType || safeComponentType || 'COMPONENT')) : 
                          (typeof selectedSkin.layoutCategory === 'object' ? 'GENERAL' : (selectedSkin.layoutCategory || 'GENERAL'))
                        }
                      </span>
                      {selectedSkin.hasPermission && (
                        <span className="badge bg-success me-2">보유중</span>
                      )}
                      {selectedSkin.tags && selectedSkin.tags.map((tag, index) => (
                        <span key={index} className="badge bg-info me-1">
                          {typeof tag === 'object' ? tag.name || tag.displayName || 'Tag' : tag}
                        </span>
                      ))}
                    </div>

                    <div className="mb-3">
                      <h6>가격</h6>
                      <span className="h5">
                        {selectedSkin.price === 0 ? '무료' : `₩${selectedSkin.price.toLocaleString()}`}
                      </span>
                    </div>

                    {selectedSkin.latestVersion && (
                      <div className="mb-3">
                        <small className="text-muted">
                          최신 버전: {typeof selectedSkin.latestVersion.version === 'object' ? 'Object version' : selectedSkin.latestVersion.version}
                        </small>
                      </div>
                    )}

                    {selectedSkin.reviewCount > 0 && (
                      <div className="mb-3">
                        <span className="text-warning">
                          {'★'.repeat(Math.round(selectedSkin.avgRating))}
                          {'☆'.repeat(5 - Math.round(selectedSkin.avgRating))}
                        </span>
                        <small className="text-muted ms-2">
                          ({selectedSkin.reviewCount}개 리뷰)
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer" style={{ 
            borderTop: '1px solid #dee2e6',
            padding: '1rem 1.5rem',
            flexShrink: 0,
            backgroundColor: '#f8f9fa'
          }}>
            {activeTab === 'detail' && selectedSkin && (
              <>
                {selectedSkin.hasPermission ? (
                  <span className="badge bg-success fs-6 py-2 px-3 me-3">
                    이미 보유중인 스킨입니다
                  </span>
                ) : (
                  <button
                    className="btn btn-success"
                    onClick={() => handlePurchase(selectedSkin.id)}
                    disabled={purchasing}
                  >
                    {purchasing ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">구매중...</span>
                        </span>
                        구매중...
                      </>
                    ) : (
                      `구매하기 ${selectedSkin.price === 0 ? '(무료)' : `(₩${selectedSkin.price.toLocaleString()})`}`
                    )}
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={purchasing}
            >
              {activeTab === 'detail' ? '닫기' : '취소'}
            </button>
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SkinPurchaseModal;